/**
 * Tyre size finder: filters the collection grid without a page reload.
 *
 * When the collection has the filter sidebar, the grid is refreshed through the theme's
 * facet-filters element so the sidebar, chips and URL stay in step; otherwise it swaps
 * #filter-results itself. This section is re-rendered via the Section Rendering API so
 * option lists and counts match the current selection. Without JavaScript the markup is a
 * plain GET form.
 */
if (!customElements.get('easyr-tyre-finder')) {
  class EasyrTyreFinder extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-tyre-form]');
      this.countEl = this.querySelector('[data-tyre-count]');
      this.clearEl = this.querySelector('[data-tyre-clear]');
      if (!this.form) return;

      // The grid this filters. Rendered by main-collection-products, which owns the id.
      this.results = document.getElementById('filter-results');
      this.resultsSectionId = this.results ? EasyrTyreFinder.sectionIdFrom(this.results) : null;

      this.fields = [...this.form.querySelectorAll('[data-tyre-select]')];
      this.bindEvents();
      window.addEventListener('popstate', () => this.apply({ push: false }));
      /* A change made in the filter sidebar or its chips swaps the grid without involving this
         section, so re-read the sizes and counts from the sidebar. Skips the update this
         section asked the sidebar for itself. */
      document.addEventListener('on:facet-filters:updated', () => {
        if (this.awaitingFacets) {
          this.awaitingFacets = false;
          return;
        }
        this.refreshFromSidebar();
      });
      // The default in-stock filter is applied after this renders; re-sync the counts. The page
      // stays hidden until this refresh lands (see easyr-default-in-stock.js).
      document.addEventListener('easyr:default-filters-applied', (evt) => {
        const refresh = this.refreshSelf();
        if (evt.detail && evt.detail.waitFor) evt.detail.waitFor(refresh);
      });
      this.loadFirstFieldRange();
      if (this.dataset.style === 'sidewall') this.initSidewall();
    }

    /**
     * Loads the unfiltered option list for the first field and keeps it for reuse.
     *
     * Shopify scopes each filter's values by every other active filter, so with profile and
     * rim chosen the width list holds only widths valid for that pair. A cascade needs each
     * field scoped only by the fields above it, so the first field must always offer every
     * size. Shopify can't return that, so it is fetched once with no filters applied.
     */
    async loadFirstFieldRange() {
      const first = this.fields[0];
      if (!first || !this.dataset.sectionId) return;
      /* With no filters in the URL the server-rendered list is already complete (the default
         in-stock filter is applied client-side, after render). */
      if (![...new URLSearchParams(window.location.search).keys()].some((k) => k.startsWith('filter.'))) {
        this.firstFieldOptions = first.innerHTML;
        return;
      }
      try {
        const res = await fetch(`${window.location.pathname}?section_id=${this.dataset.sectionId}`);
        if (!res.ok) return;
        const tmpl = document.createElement('template');
        tmpl.innerHTML = await res.text();
        const match = tmpl.content.querySelector(`[name="${CSS.escape(first.name)}"]`);
        if (match) {
          this.firstFieldOptions = match.innerHTML;
          this.restoreFirstField();
        }
      } catch (err) {
        // Non-fatal: filtering still works, but the first field stays scoped.
        console.warn('[easyr-tyre-finder] could not load the full size range', err);
      }
    }

    /** Puts the first field's full option list back, keeping its selection if still present. */
    restoreFirstField() {
      const first = this.fields[0];
      if (!first || !this.firstFieldOptions) return;
      const keep = first.value;
      first.innerHTML = this.firstFieldOptions;
      if ([...first.options].some((o) => o.value === keep)) first.value = keep;
    }

    /** Reads the numeric section id out of a `shopify-section-<id>` wrapper. */
    static sectionIdFrom(el) {
      const wrapper = el.closest('[id^="shopify-section-"]');
      return wrapper ? wrapper.id.replace('shopify-section-', '') : null;
    }

    bindEvents() {
      this.form.addEventListener('change', (evt) => {
        if (!evt.target.matches('[data-tyre-select]')) return;
        this.clearDownstream(evt.target);
        this.syncSidewall();
        this.apply();
      });

      if (this.clearEl) {
        this.clearEl.addEventListener('click', (evt) => {
          if (!this.results) return; // let the link navigate normally without JS support
          evt.preventDefault();
          this.form.querySelectorAll('[data-tyre-select]').forEach((s) => {
            s.value = '';
          });
          this.syncSidewall();
          this.apply();
        });
      }
    }

    /**
     * Clears every field after the one that changed.
     *
     * Shopify scopes each filter's values by the other active filters, so without this a
     * width valid with a different profile/rim would be missing from the list entirely.
     * Clearing downstream leaves profile scoped by width and rim by width and profile.
     * @param {HTMLSelectElement} changed - The field the shopper changed.
     */
    clearDownstream(changed) {
      const fields = [...this.form.querySelectorAll('[data-tyre-select]')];
      const from = fields.indexOf(changed);
      if (from === -1) return;
      fields.slice(from + 1).forEach((select) => {
        select.value = '';
      });
    }

    /** The theme's filter sidebar, when filtering is enabled on this collection. */
    get facets() {
      const el = document.querySelector('facet-filters');
      return el && el.form && typeof el.applyFilters === 'function' ? el : null;
    }

    /**
     * Builds the query: every filter applied in the sidebar form, plus sort, with the size
     * fields overriding their own keys. The sidebar form is the source of truth because the
     * default in-stock filter lives only there, not in the URL.
     * @returns {URLSearchParams}
     */
    searchParams() {
      const params = new URLSearchParams();
      const own = new Set(this.fields.map((f) => f.name));
      const facets = this.facets;
      if (facets) {
        new URLSearchParams(new FormData(facets.form)).forEach((value, key) => {
          if (value && !own.has(key)) params.append(key, value);
        });
      }
      const sort = new URLSearchParams(window.location.search).get('sort_by');
      if (sort && !params.has('sort_by')) params.set('sort_by', sort);
      this.form.querySelectorAll('[data-tyre-select]').forEach((select) => {
        // An empty value means "any": omit the key rather than send it blank.
        if (select.value) params.set(select.name, select.value);
      });
      return params;
    }

    /**
     * Applies the current selection to the grid and refreshes this section's fields.
     * Falls back to submitting the form when the grid isn't on the page.
     * @param {Object} [options]
     * @param {boolean} [options.push=true] - Build the query from the fields and add a history
     *   entry. False on popstate, where the URL is already current.
     */
    async apply({ push = true } = {}) {
      if (!this.results || !this.resultsSectionId) {
        this.form.submit();
        return;
      }

      const params = push ? this.searchParams() : new URLSearchParams(window.location.search);
      const qs = params.toString();
      const base = `${window.location.pathname}${qs ? `?${qs}` : ''}`;

      /* With the sidebar present, applyFilters() swaps the grid, sidebar and chips together
         and records history. It handles popstate itself, so only refresh this section. */
      const facets = this.facets;
      if (facets) {
        if (push) {
          this.awaitingFacets = true;
          /* applyFilters() reads the focused element's id as it starts and focuses that element
             again once the grid lands. By then the next field may be open, and moving focus
             would close it, so the id is hidden while the call starts. Focus stays put. */
          const focused = this.contains(document.activeElement) ? document.activeElement : null;
          const focusedId = focused ? focused.id : '';
          if (focusedId) focused.removeAttribute('id');
          facets.applyFilters(qs, null, true);
          if (focusedId) focused.id = focusedId;
        }
        this.setBusy(true);
        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();
        const ticket = this.nextTicket();
        try {
          const selfHtml = await this.fetchSection(this.dataset.sectionId, qs);
          if (selfHtml && ticket === this.ticket) this.refreshFields(selfHtml);
        } catch (err) {
          if (err.name !== 'AbortError') console.warn('[easyr-tyre-finder] could not refresh sizes', err);
        } finally {
          if (ticket === this.ticket) this.setBusy(false);
        }
        return;
      }

      this.results.classList.add('is-loading');
      this.setBusy(true);

      // A new selection supersedes whatever is in flight.
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      const ticket = this.nextTicket();
      try {
        const [gridHtml, selfHtml] = await Promise.all([
          this.fetchSection(this.resultsSectionId, qs),
          this.fetchSection(this.dataset.sectionId, qs)
        ]);

        if (gridHtml) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = gridHtml;
          const fresh = tmpl.content.getElementById('filter-results');
          if (fresh) this.results.innerHTML = fresh.innerHTML;
        }

        if (selfHtml && ticket === this.ticket) this.refreshFields(selfHtml);

        if (push) EasyrTyreFinder.updateURL(qs);
      } catch (err) {
        if (err.name !== 'AbortError') {
          // Navigate rather than leave a grid that doesn't match the fields.
          console.warn('[easyr-tyre-finder] falling back to navigation', err);
          window.location.href = base;
          return;
        }
      } finally {
        this.results.classList.remove('is-loading');
        this.setBusy(false);
      }
    }

    /** Re-render only this widget's fields and count against the currently applied filters. */
    /** Re-renders the fields and count for the filters applied in the sidebar, sizes included. */
    async refreshFromSidebar() {
      const facets = this.facets;
      if (!facets) return;
      const params = new URLSearchParams();
      new URLSearchParams(new FormData(facets.form)).forEach((value, key) => {
        if (value) params.append(key, value);
      });
      const sort = new URLSearchParams(window.location.search).get('sort_by');
      if (sort && !params.has('sort_by')) params.set('sort_by', sort);
      const ticket = this.nextTicket();
      try {
        const html = await this.fetchSection(this.dataset.sectionId, params.toString());
        if (html && ticket === this.ticket) this.refreshFields(html);
      } catch (err) {
        if (err.name !== 'AbortError') console.warn('[easyr-tyre-finder] could not refresh sizes', err);
      }
    }

    /**
     * Numbers each refresh of this section. Cached renders can't be aborted, so a slow earlier
     * one could land after a newer one; only the latest ticket is applied.
     * @returns {number}
     */
    nextTicket() {
      this.ticket = (this.ticket || 0) + 1;
      return this.ticket;
    }

    async refreshSelf() {
      const ticket = this.nextTicket();
      try {
        const html = await this.fetchSection(this.dataset.sectionId, this.searchParams().toString());
        if (html && ticket === this.ticket) this.refreshFields(html);
      } catch (err) {
        if (err.name !== 'AbortError') console.warn('[easyr-tyre-finder] could not refresh sizes', err);
      }
    }

    /**
     * Fetches a section rendered against the given query.
     * @param {string} sectionId - Section id.
     * @param {string} qs - Query string, without the leading "?".
     * @returns {Promise<?string>} Section HTML, or null if unavailable.
     */
    async fetchSection(sectionId, qs) {
      if (!sectionId) return null;
      // This section's own renders are kept for the visit (see prefetchNext), so a prefetched next
      // step, Back/Forward or picking a size again needs no request.
      if (sectionId === this.dataset.sectionId) return this.cachedSection(qs);
      const url = `${window.location.pathname}?${qs}${qs ? '&' : ''}section_id=${sectionId}`;
      const res = await fetch(url, this.abortController ? { signal: this.abortController.signal } : {});
      return res.ok ? res.text() : null;
    }

    /**
     * This section rendered for a query, from the visit's cache or fetched (and cached).
     * Cached requests aren't tied to the abort controller: a prefetch outlives the selection
     * that started it. Failures are dropped from the cache so they're retried.
     * @param {string} qs - Query string, as built by searchParams().
     * @returns {Promise<?string>}
     */
    cachedSection(qs) {
      this.sectionCache = this.sectionCache || new Map();
      if (!this.sectionCache.has(qs)) {
        const url = `${window.location.pathname}?${qs}${qs ? '&' : ''}section_id=${this.dataset.sectionId}`;
        const request = fetch(url)
          .then((res) => (res.ok ? res.text() : null))
          .then((html) => {
            if (html === null) this.sectionCache.delete(qs);
            return html;
          })
          .catch((err) => {
            this.sectionCache.delete(qs);
            throw err;
          });
        this.sectionCache.set(qs, request);
      }
      return this.sectionCache.get(qs);
    }

    /**
     * Renders, in the background, every state the next pick can lead to, so that pick shows at
     * once instead of after a round trip. Only when the next field has a short list (profile and
     * rim do; the full width list doesn't).
     */
    prefetchNext() {
      clearTimeout(this.prefetchTimer);
      const next = this.fields.findIndex((f) => !f.value);
      if (next === -1) return;
      const field = this.fields[next];
      const values = [...field.options].map((o) => o.value).filter(Boolean);
      if (!values.length || values.length > 15) return;
      const later = this.fields.slice(next + 1).map((f) => f.name);
      const run = () => {
        values.forEach((value) => {
          const params = this.searchParams();
          params.set(field.name, value);
          later.forEach((name) => params.delete(name));
          this.cachedSection(params.toString()).catch(() => {});
        });
      };
      this.prefetchTimer = setTimeout(run, 100);
    }

    /**
     * Replaces each select's options, the count and the clear link's visibility from a
     * freshly rendered copy of this section.
     * @param {string} html - Section HTML from fetchSection().
     */
    refreshFields(html) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      const fresh = tmpl.content.querySelector('easyr-tyre-finder');
      if (!fresh) return;

      /* The fresh section was rendered for the URL that was just fetched, so its selected options
         are the sizes now applied, including after Back/Forward, when the fields still hold
         the previous ones. */
      this.form.querySelectorAll('[data-tyre-select]').forEach((select) => {
        const match = fresh.querySelector(`[name="${CSS.escape(select.name)}"]`);
        if (!match) return;
        const chosen = match.querySelector('option[selected]');
        const keep = chosen ? chosen.value : '';
        select.innerHTML = match.innerHTML;
        select.value = [...select.options].some((o) => o.value === keep) ? keep : '';
      });

      // The response scopes the first field too; restore its full list.
      this.restoreFirstField();

      // innerHTML: the count is a number element plus a label element.
      const freshCount = fresh.querySelector('[data-tyre-count]');
      if (freshCount && this.countEl) this.countEl.innerHTML = freshCount.innerHTML;

      if (this.clearEl) {
        this.clearEl.hidden = ![...this.form.querySelectorAll('[data-tyre-select]')].some((s) => s.value);
      }

      this.syncSidewall();
      this.prefetchNext();
    }

    /**
     * Sets up the Sidewall style: the selects sit invisibly over sockets on the tyre drawing
     * (snippets/easyr-tyre-sidewall.liquid), and the "Choose width" button under it carries a
     * copy of the next field's select. The field selects remain the source of truth; the
     * drawing and the button only mirror them.
     */
    initSidewall() {
      this.swTyre = this.querySelector('[data-sw-tyre]');
      if (!this.swTyre) return;
      this.swHelp = this.querySelector('[data-sw-help]');
      this.swStep = this.querySelector('[data-sw-step]');
      this.swStepLabel = this.querySelector('[data-sw-step-label]');
      this.swStepSelect = this.querySelector('[data-sw-step-select]');
      this.swShow = this.querySelector('[data-sw-show]');
      this.swShowLabel = this.querySelector('[data-sw-show-label]');
      if (this.swShow) this.swShow.addEventListener('click', () => this.scrollToResults());
      this.swLabels = (this.dataset.labels || '').split(',').map((l) => l.trim());
      if (this.swStepSelect) {
        // Picking from the button sets the field it stands in for, which then filters as usual.
        this.swStepSelect.addEventListener('change', () => {
          const field = this.fields[this.swNext];
          if (!field) return;
          field.value = this.swStepSelect.value;
          field.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
      this.syncSidewall();
      // Text metrics change once the heading font loads, and the drawing scales with its container.
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.placeSidewallFields());
      if ('ResizeObserver' in window) {
        new ResizeObserver(() => this.placeSidewallFields()).observe(this.swTyre);
      }
    }

    /** The chosen option's text without the "(12)" count suffix. */
    static optionText(select) {
      const opt = select.options[select.selectedIndex];
      return opt && select.value ? opt.textContent.replace(/\s*\(\d+\)\s*$/, '').trim() : '';
    }

    /**
     * Writes the chosen values into the sockets, marks the next one to fill, and updates the
     * step line and the button under the tyre.
     */
    syncSidewall() {
      if (!this.swTyre) return;
      const values = this.fields.map((f) => EasyrTyreFinder.optionText(f));
      const next = values.findIndex((v) => !v);
      this.swNext = next;

      values.forEach((value, i) => {
        const state = value ? 'done' : i === next ? 'next' : 'todo';
        this.swTyre.querySelectorAll(`[data-sw-socket="${i}"]`).forEach((socket) => {
          socket.dataset.state = state;
          const valueEl = socket.querySelector('[data-sw-value]');
          if (valueEl) valueEl.textContent = value;
        });
        const field = this.swTyre.querySelector(`[data-sw-field="${i}"]`);
        if (field) field.dataset.state = state;
      });

      if (this.swHelp) {
        const b = document.createElement('b');
        this.swHelp.replaceChildren();
        if (next === -1) {
          const seps = (this.dataset.seps || '/,R').split(',').map((s) => s.trim());
          b.textContent = `${values[0]}/${values[1]} ${seps[1] || 'R'}${values[2]}`;
          this.swHelp.append(b, ` · ${this.dataset.helpDone || ''}`);
        } else {
          b.textContent = (this.dataset.step || '')
            .replace('[n]', String(next + 1))
            .replace('[total]', String(this.fields.length));
          this.swHelp.append(b, ` · ${this.dataset[`help-${next}`] || this.dataset[`help${next}`] || ''}`);
        }
        this.swHelp.dataset.done = String(next === -1);
      }

      if (this.swStep) {
        this.swStep.hidden = next === -1;
        if (next !== -1) {
          const label = `${this.dataset.choose || ''} ${(this.swLabels[next] || '').toLowerCase()}`.trim();
          if (this.swStepLabel) this.swStepLabel.textContent = label;
          if (this.swStepSelect) {
            this.swStepSelect.innerHTML = this.fields[next].innerHTML;
            this.swStepSelect.value = '';
            this.swStepSelect.setAttribute('aria-label', label);
          }
        }
      }

      if (this.swShow) {
        const countNum = this.countEl && this.countEl.querySelector('.easyr-tyre-finder__count-num');
        const count = countNum ? countNum.textContent.trim() : '';
        this.swShow.hidden = next !== -1 || !count || count === '0';
        if (this.swShowLabel) this.swShowLabel.textContent = (this.dataset.show || '').replace('[count]', count);
      }

      this.placeSidewallFields();
    }

    /** Scrolls the results into view below the sticky header. */
    scrollToResults() {
      if (!this.results) return;
      // The header sections that stay on screen (sticky or fixed) would cover the top of the grid.
      const covered = [...document.querySelectorAll('.shopify-section-group-header-group')]
        .filter((el) => ['sticky', 'fixed'].includes(getComputedStyle(el).position))
        .reduce((max, el) => Math.max(max, el.getBoundingClientRect().height), 0);
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({
        top: this.results.getBoundingClientRect().top + window.scrollY - covered - 12,
        behavior: reduce ? 'auto' : 'smooth'
      });
    }

    /**
     * Positions each select over its socket, at least 44px each way for touch. Measures
     * whichever of the two drawings CSS is showing.
     */
    placeSidewallFields() {
      if (!this.swTyre) return;
      const svg = [...this.swTyre.querySelectorAll('[data-sw-svg]')].find((el) => el.getBoundingClientRect().width > 0);
      if (!svg) return;
      const box = this.swTyre.getBoundingClientRect();
      this.fields.forEach((select, i) => {
        const field = select.closest('[data-sw-field]');
        const socket = svg.querySelector(`[data-sw-hit="${i}"]`);
        if (!field || !socket) return;
        const { left, right, top, bottom } = socket.getBoundingClientRect();
        const w = Math.max(right - left + 8, 44);
        const h = Math.max(bottom - top + 8, 44);
        const cx = (left + right) / 2 - box.left;
        const cy = (top + bottom) / 2 - box.top;
        Object.assign(field.style, {
          left: `${Math.round(cx - w / 2)}px`,
          top: `${Math.round(Math.max(0, cy - h / 2))}px`,
          width: `${Math.round(w)}px`,
          height: `${Math.round(h)}px`
        });
      });
    }

    /**
     * Locks the fields while the next list loads. The loading state only shows if the wait is
     * long enough to notice, so a list already prefetched doesn't flash it.
     * @param {boolean} busy
     */
    setBusy(busy) {
      this.classList.toggle('is-busy', busy);
      clearTimeout(this.waitingTimer);
      if (busy) {
        this.waitingTimer = setTimeout(() => this.classList.add('is-waiting'), 150);
      } else {
        this.classList.remove('is-waiting');
      }
      this.form.querySelectorAll('[data-tyre-select]').forEach((s) => {
        s.setAttribute('aria-busy', String(busy));
      });
    }

    static updateURL(qs) {
      window.history.pushState({ tyreFinder: qs }, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
    }
  }

  customElements.define('easyr-tyre-finder', EasyrTyreFinder);
}
